import '../styles/DashboardRecentRuns.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

/** 2026-02-11T14:22:00 → "2026-02-11 14:22" (local store values render verbatim, no timezone shifting). */
function formatStamp(value) {
  if (!value) return '—';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return String(value);
  return `${m[1]}-${m[2]}-${m[3]}${m[4] ? ` ${m[4]}:${m[5]}` : ''}`;
}

const STATE_LABEL = { complete: 'Complete', failed: 'Failed', running: 'Running' };

function StateBadge({ state }) {
  const key = STATE_LABEL[state] ? state : 'unknown';
  const label = STATE_LABEL[state] || String(state || 'Unknown');
  return (
    <span className={`drr-badge drr-badge--${key}`}>
      {state === 'running' && <span className="drr-badge__live" aria-hidden="true" />}
      {label}
    </span>
  );
}

function RecentRunLoading() {
  return (
    <div role="status" aria-live="polite" aria-label="Reading recent runs from local storage">
      <ul className="drr-list">
        {[1, 2, 3].map((n) => (
          <li key={`skeleton-${n}`} className="drr-row drr-row--skeleton" aria-hidden="true">
            <div className="drr-run">
              <span className="drr-sk drr-sk--name" />
              <span className="drr-sk drr-sk--target" />
            </div>
            <div className="drr-config"><span className="drr-sk drr-sk--config" /></div>
            <span className="drr-sk drr-sk--badge" />
          </li>
        ))}
      </ul>
      <span className="drr-loading-note">Reading local store…</span>
    </div>
  );
}

function RecentRunEmpty() {
  return (
    <div className="drr-empty">
      <span className="drr-empty__mark" aria-hidden="true">0</span>
      <h3 className="drr-empty__h3">No runs yet</h3>
      <p className="drr-empty__copy">
        Configure an authorized target, choose a wordlist or a mask, and Brute will record the run here.
      </p>
      <a className="drr-cta" href="/new-test-run">Start a new test run</a>
    </div>
  );
}

function RecentRunError({ message, onRetry }) {
  return (
    <div className="drr-error" role="alert">
      <span className="drr-error__label">Local store read failed</span>
      <p className="drr-error__msg">
        Brute could not read the run history from local storage. Nothing has been changed.
      </p>
      {message && <p className="drr-error__detail">{message}</p>}
      <button type="button" className="drr-btn drr-btn--primary" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function RecentRunList({ runs }) {
  return (
    <ul className="drr-list">
      {runs.map((run) => (
        <li key={run.id} className="drr-row">
          <div className="drr-row__top">
            <div className="drr-run">
              <span className="drr-run__name">{run.name}</span>
              <span className="drr-run__target">{run.target}</span>
            </div>
            <StateBadge state={run.state} />
          </div>
          <p className="drr-config">{run.configurationSummary}</p>
          <div className="drr-meta">
            <time className="drr-time" dateTime={run.createdAt}>{formatStamp(run.createdAt)}</time>
          </div>
          <div className="drr-actions">
            <a
              className="drr-btn drr-btn--ghost"
              href={`/run-monitor?id=${encodeURIComponent(run.id)}`}
              aria-label={`Monitor ${run.name}`}
            >
              Monitor
            </a>
            <a
              className="drr-btn drr-btn--solid"
              href={`/results?id=${encodeURIComponent(run.id)}`}
              aria-label={`Results for ${run.name}`}
            >
              Results
            </a>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function DashboardRecentRuns() {
  const { data, loading, error, refresh } = useProjectCollection('test_run');
  const [retrying, setRetrying] = useState(false);

  const runs = [...(data || [])].sort(
    (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
  );

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  let body;
  if (loading) body = <RecentRunLoading />;
  else if (error) {
    const message = typeof error === 'string' ? error : (error && error.message) || '';
    body = <RecentRunError message={message} onRetry={handleRetry} />;
  }
  else if (!runs.length) body = <RecentRunEmpty />;
  else body = <RecentRunList runs={runs} />;

  return (
    <section className="drr-section" aria-labelledby="drr-title">
      <div className="drr-panel">
        <header className="drr-head">
          <div className="drr-head__title">
            <span className="drr-head__kicker">Local store · run history</span>
            <h2 className="drr-head__h2" id="drr-title">Recent runs</h2>
          </div>
          <div className="drr-head__right">
            {!loading && !error && runs.length > 0 && (
              <span className="drr-head__count">{runs.length} recorded</span>
            )}
            <span className="drr-sticker">Authorized target only</span>
          </div>
        </header>
        {body}
      </div>
    </section>
  );
}